import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { SectionBadge } from '../../components/SectionHeading'
import DayTripCard from '../../components/DayTripCard'
import useSavedPlaces from '../../hooks/useSavedPlaces'
import { apiGetDayTrips, shouldShowOnHomepage, type DayTrip } from '../../api/dayTrips/dayTripApi'

function ArrowCircleButton({
  direction,
  onClick,
  className = '',
}: {
  direction: 'left' | 'right'
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === 'left' ? 'Scroll left' : 'Scroll right'}
      className={`grid place-items-center rounded-full bg-[#E2E2E2] shadow-[0_2px_10px_rgba(0,0,0,0.12)] transition-all duration-200 hover:scale-105 hover:bg-[#d4d4d4] hover:shadow-md active:scale-95 ${className}`}
    >
      <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        className={direction === 'left' ? 'rotate-180' : ''}
        aria-hidden="true"
      >
        <path
          d="M8 4L16 12L8 20"
          stroke="#171717"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  )
}

function DayTripsSidebar() {
  return (
    <motion.div 
      variants={{
        hidden: { opacity: 0, x: -30 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
      }}
      className="hidden w-[330px] shrink-0 flex-col gap-2 lg:flex"
    >
      <div className="overflow-hidden rounded-[16px] bg-white shadow-[0_2px_12px_rgba(0,0,0,0.12)]">
        <img
          src="/assets/images/day-trips-sidebar.png"
          alt="Day trips"
          className="h-[372px] w-full object-cover "
        />
      </div>

      <div className="rounded-[10px] bg-[#3E68A1] p-5 text-white">
        <h3 className="font-host text-[22px] font-semibold leading-[1.15] tracking-[-0.02em]">
          Day Trips
        </h3>
        <p className="mt-3 max-w-[320px] font-host text-[15px] leading-[1.38] tracking-[-0.02em]">
          Visit Austria&apos;s top destinations with a professional driver, comfortable vehicle
          and convenient hotel pickup.
        </p>
      </div>
    </motion.div>
  )
}

function DayTripsCarousel({ dayTrips }: { dayTrips: DayTrip[] }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const navigate = useNavigate()
  const { isSaved, toggleSaved } = useSavedPlaces()

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return

    const amount = direction === 'left' ? -320 : 320

    scrollRef.current.scrollBy({
      left: amount,
      behavior: 'smooth',
    })

    window.setTimeout(() => {
      if (!scrollRef.current) return
      setCanScrollLeft(scrollRef.current.scrollLeft > 5)
    }, 350)
  }

  return (
    <div className="relative min-w-0 flex-1">
      <motion.div
        ref={scrollRef}
        variants={{
          hidden: { opacity: 0, x: 30 },
          visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
        }}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-4 pr-2 scrollbar-hide lg:gap-3.5 lg:pb-0"
      >
        {dayTrips.map((trip) => (
          <DayTripCard
            key={trip.id}
            id={trip.id}
            title={trip.title}
            subtitle={trip.subtitle}
            description={trip.shortDescription}
            image={trip.images?.[0] || ''}
            tags={trip.highlights || []}
            price={String(trip.price)}
            isSaved={isSaved(trip.id)}
            onSaveToggle={() =>
              toggleSaved({
                id: trip.id,
                source: 'day-trip',
                title: trip.title,
                duration: trip.duration,
                price: String(trip.price),
                image: trip.images?.[0] || '',
                explorePath: `/day-trips?id=${trip.id}`,
              })
            }
            onExplore={() => navigate(`/day-trips?id=${trip.id}`)}
          />
        ))}
      </motion.div>

      {canScrollLeft && (
        <div className="absolute left-[-40px] top-1/2 hidden -translate-y-1/2 lg:block">
          <ArrowCircleButton
            direction="left"
            onClick={() => scroll('left')}
            className="h-[64px] w-[64px]"
          />
        </div>
      )}

      <div className="absolute right-[-40px] top-1/2 hidden -translate-y-1/2 lg:block">
        <ArrowCircleButton
          direction="right"
          onClick={() => scroll('right')}
          className="h-[64px] w-[64px]"
        />
      </div>
    </div>
  )
}

export default function PopularTrips() {
  const [dayTrips, setDayTrips] = useState<DayTrip[]>([])

  useEffect(() => {
    async function load() {
      const res = await apiGetDayTrips()
      if (res.data && res.data.length > 0) {
        const filtered = res.data.filter(shouldShowOnHomepage)
        setDayTrips(filtered.length > 0 ? filtered : res.data)
      }
    }
    load()
  }, [])

  if (dayTrips.length === 0) return null

  return (
    <section className="bg-white py-14 lg:py-20 overflow-hidden">
      <motion.div 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.1,
            },
          },
        }}
        className="mx-auto max-w-[1540px] px-4 lg:px-[76px]"
      >
        <motion.div 
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
          }}
          className="flex flex-col items-center text-center"
        >
          <SectionBadge className="mb-4 h-[31px] rounded-full px-5">
            Our Trips
          </SectionBadge>

          <h2 className="font-host text-[24px] font-semibold leading-[1.25] tracking-[-0.02em] text-[#3C3838] lg:text-[30px]">
            Popular Day Trips from Salzburg
          </h2>

          <p className="mt-3 max-w-[520px] font-host text-[16px] font-medium leading-[1.45] tracking-[-0.03em] text-[#8A8686]">
            Visit Austria&apos;s top attractions with private, comfortable and hassle-free day
            trips.
          </p>
        </motion.div>

        <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:gap-3.5">
          <DayTripsSidebar />
          <DayTripsCarousel dayTrips={dayTrips} />
        </div>
      </motion.div>
    </section>
  )
}
